import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
    ConversationRatingDetailDto,
    GroundTeamUserRatingsSummaryDto,
} from '../../../model/brokerConnections/IBrokerConnectionGroundTeamRatings';
import {
    RatingsFilterParams,
    getUserRatingDetails,
} from '../../../store/features/groundTeamRatings/groundTeamRatingsApi';
import {
    DataGrid,
    DataGridHeader,
    DataGridRow,
    DataGridHeaderCell,
    DataGridCell,
    DataGridBody,
    Body1,
    Caption1,
    createTableColumn,
    TableColumnDefinition,
    TableCellLayout,
    Tooltip,
    Button,
    Card,
    Spinner,
    Text,
    MessageBar,
    MessageBarBody,
    Title3,
} from '@fluentui/react-components';
import {
    ChevronLeft24Regular,
    ErrorCircle24Regular,
    NumberRow16Regular,
    AirplaneTakeOffRegular,
    Person16Regular,
    ChatMultiple16Regular,
    Star16Regular,
    Timer16Regular,
    HandRight16Regular,
    CheckboxChecked16Regular,
    ChevronDown16Regular,
    ChevronUp16Regular,
    People16Regular,
} from '@fluentui/react-icons';
import RatingStars from '../RatingStars/RatingStars';
import { useUserRatingsDetailListStyles } from './UserRatingsDetailList.styles';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    formatElapsedMilliseconds,
    formatCompactDateTime,
} from '../../../utils/formatters';
import { app } from '@microsoft/teams-js';

interface ConversationRatingDetailProps {
    user: GroundTeamUserRatingsSummaryDto;
    onBack: () => void;
    dateFilter?: RatingsFilterParams;
}

const getColumns = (
    t: TFunction,
    styles: ReturnType<typeof useUserRatingsDetailListStyles>,
    expandedComments: Set<string>,
    toggleCommentExpansion: (rowId: string) => void
): TableColumnDefinition<ConversationRatingDetailDto>[] => {
    return [
        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'flightNumber',
            renderHeaderCell: () => (
                <TableCellLayout media={<NumberRow16Regular />}>
                    {t('TableHeader_Flight') || 'Flight #'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                return (
                    <TableCellLayout>
                        <Body1>{`BA${item.flightNumber}` || '-'}</Body1>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (a.flightNumber || '').localeCompare(
                    b.flightNumber || ''
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'departureUtc',
            renderHeaderCell: () => (
                <TableCellLayout media={<AirplaneTakeOffRegular />}>
                    {t('TableHeader_DepartureTime') || 'Scheduled Departure'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                const formattedDate = formatCompactDateTime(
                    item.scheduledDepartureUtc
                );
                return (
                    <TableCellLayout>
                        <Body1>{formattedDate}</Body1>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (a.scheduledDepartureUtc || '').localeCompare(
                    b.scheduledDepartureUtc || ''
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'initiatedFrom',
            renderHeaderCell: () => (
                <TableCellLayout media={<People16Regular />}>
                    {t('TableHeader_InitiatedFrom') || 'Initiated From'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                return (
                    <TableCellLayout>
                        <Body1>{item.initiatedFrom}</Body1>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (a.initiatedFrom || '').localeCompare(
                    b.initiatedFrom || ''
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'rating',
            renderHeaderCell: () => (
                <TableCellLayout media={<Star16Regular />}>
                    {t('Table_Header_Ratings_Rating') || 'Rating'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                return (
                    <TableCellLayout>
                        {item.ratingFromFlightCrew ? (
                            <Tooltip
                                content={
                                    item.ratingFromFlightCrewOnUtc?.toString()
                                        ? `Rated at ${item.ratingFromFlightCrewOnUtc?.toString()}`
                                        : ''
                                }
                                relationship="description"
                                positioning="above"
                            >
                                <RatingStars
                                    rating={item.ratingFromFlightCrew}
                                />
                            </Tooltip>
                        ) : (
                            <Caption1>
                                {t('ConversationRatings_NotRated') ||
                                    'Not rated'}
                            </Caption1>
                        )}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (
                    (a.ratingFromFlightCrew ?? 0) -
                    (b.ratingFromFlightCrew ?? 0)
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'firstResponseUser',
            renderHeaderCell: () => (
                <TableCellLayout media={<Person16Regular />}>
                    {t('ConversationRatings_FirstResponder') ||
                        'First Responder'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                return (
                    <TableCellLayout>
                        <Body1>{item.firstResponseUser || '-'}</Body1>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (a.firstResponseUser || '').localeCompare(
                    b.firstResponseUser || ''
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'firstResponseTime',
            renderHeaderCell: () => (
                <TableCellLayout media={<Timer16Regular />}>
                    {t('ConversationRatings_FirstResponseTime') ||
                        'First Response Time'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                const formattedTime = item.firstResponseTimeSeconds
                    ? formatElapsedMilliseconds(
                          item.firstResponseTimeSeconds * 1000
                      )
                    : '-';
                return (
                    <TableCellLayout>
                        <Body1>{formattedTime}</Body1>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (
                    (a.firstResponseTimeSeconds ?? 0) -
                    (b.firstResponseTimeSeconds ?? 0)
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'handlingTime',
            renderHeaderCell: () => (
                <TableCellLayout media={<HandRight16Regular />}>
                    {t('ConversationRatings_HandlingTime') || 'Handling Time'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                const formattedTime = item.handlingTimeSeconds
                    ? formatElapsedMilliseconds(item.handlingTimeSeconds * 1000)
                    : '-';
                return (
                    <TableCellLayout>
                        <Body1>{formattedTime}</Body1>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (
                    (a.handlingTimeSeconds ?? 0) - (b.handlingTimeSeconds ?? 0)
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'resolutionTime',
            renderHeaderCell: () => (
                <TableCellLayout media={<CheckboxChecked16Regular />}>
                    {t('ConversationRatings_ResolutionTime') ||
                        'Resolution Time'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                const formattedTime = item.resolutionTimeSeconds
                    ? formatElapsedMilliseconds(
                          item.resolutionTimeSeconds * 1000
                      )
                    : '-';
                return (
                    <TableCellLayout>
                        <Body1>{formattedTime}</Body1>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (
                    (a.resolutionTimeSeconds ?? 0) -
                    (b.resolutionTimeSeconds ?? 0)
                );
            },
        }),

        createTableColumn<ConversationRatingDetailDto>({
            columnId: 'comments',
            renderHeaderCell: () => (
                <TableCellLayout media={<ChatMultiple16Regular />}>
                    {t('Table_Header_Ratings_Comments') || 'Comments'}
                </TableCellLayout>
            ),
            renderCell: (item: ConversationRatingDetailDto) => {
                const comments = item.commentsFromFlightCrew || '-';
                const rowId = item.conversationId;
                const isExpanded = expandedComments.has(rowId);
                const maxLength = 100;
                const needsTruncation =
                    comments.length > maxLength && comments !== '-';

                return (
                    <TableCellLayout>
                        <div className={styles.commentCell}>
                            <Body1
                                className={
                                    isExpanded
                                        ? styles.expandedText
                                        : needsTruncation
                                          ? styles.truncatedText
                                          : undefined
                                }
                            >
                                {comments}
                            </Body1>
                            {needsTruncation && (
                                <Button
                                    appearance="transparent"
                                    size="small"
                                    icon={
                                        isExpanded ? (
                                            <ChevronUp16Regular />
                                        ) : (
                                            <ChevronDown16Regular />
                                        )
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleCommentExpansion(rowId);
                                    }}
                                    data-stop-row-click
                                    className={styles.expandButton}
                                >
                                    {isExpanded
                                        ? t('Common_ShowLess') || 'Show less'
                                        : t('Common_ShowMore') || 'Show more'}
                                </Button>
                            )}
                        </div>
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                return (a.commentsFromFlightCrew || '').localeCompare(
                    b.commentsFromFlightCrew || ''
                );
            },
        }),
    ];
};

const columnSizingOptions = {
    flightNumber: {
        defaultWidth: 100,
        minWidth: 90,
    },
    departureUtc: {
        defaultWidth: 130,
        minWidth: 120,
    },
    initiatedFrom: {
        defaultWidth: 130,
        minWidth: 80,
    },
    rating: {
        defaultWidth: 100,
        minWidth: 90,
    },
    firstResponseUser: {
        defaultWidth: 150,
        minWidth: 140,
    },
    firstResponseTime: {
        defaultWidth: 170,
        minWidth: 120,
    },
    handlingTime: {
        defaultWidth: 140,
        minWidth: 90,
    },
    resolutionTime: {
        defaultWidth: 140,
        minWidth: 90,
    },
    comments: {
        defaultWidth: 250,
        minWidth: 150,
    },
};

const UserRatingsDetailList: React.FC<ConversationRatingDetailProps> = ({
    user,
    onBack,
    dateFilter,
}) => {
    const styles = useUserRatingsDetailListStyles();
    const { t } = useTranslation();
    const [conversations, setConversations] = useState<
        ConversationRatingDetailDto[]
    >([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedComments, setExpandedComments] = useState<Set<string>>(
        new Set()
    );

    const toggleCommentExpansion = useCallback((rowId: string) => {
        setExpandedComments((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    }, []);

    const columns = useMemo(
        () => getColumns(t, styles, expandedComments, toggleCommentExpansion),
        [t, styles, expandedComments, toggleCommentExpansion]
    );

    useEffect(() => {
        const loadUserRatings = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await getUserRatingDetails(
                    user.userId,
                    dateFilter
                );
                setConversations(response);
            } catch (err) {
                const errorMsg =
                    err instanceof Error
                        ? err.message
                        : 'An unexpected error occurred';
                setError(errorMsg);
                console.error('Error loading user ratings:', err);
            } finally {
                setLoading(false);
            }
        };

        loadUserRatings();
    }, [user.userId, dateFilter]);

    const handleRowClick = useCallback((conversationLink: string) => {
        if (!conversationLink) return;
        (async () => {
            try {
                await app.initialize();
                const ctx = await app.getContext();
                if (!ctx) throw new Error('No Teams context');
                await app.openLink(conversationLink);
            } catch {
                // Fallback: open in new tab when not running inside Teams or on error
                window.open(conversationLink, '_blank', 'noopener');
            }
        })();
    }, []);

    const handleRowClickEvent = (
        e: React.MouseEvent,
        item: ConversationRatingDetailDto
    ) => {
        const target = e.target as HTMLElement;
        if (
            target.closest(
                'button, [data-stop-row-click], input[type="checkbox"]'
            )
        ) {
            return;
        }
        handleRowClick(item.conversationTeamsLink);
    };

    if (loading) {
        return (
            <div className={styles.centerContent}>
                <Spinner
                    label={
                        t('ConversationRatings_LoadingUserRatings') ||
                        'Loading ratings for user...'
                    }
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <Button
                    icon={<ChevronLeft24Regular />}
                    appearance="subtle"
                    onClick={onBack}
                    className={styles.backButton}
                >
                    {t('Common_BackToOverview') || 'Back to Overview'}
                </Button>
                <MessageBar intent="error" icon={<ErrorCircle24Regular />}>
                    <MessageBarBody>
                        <Text weight="semibold">
                            {t('ConversationRatings_ErrorLoading') ||
                                'Error loading user ratings'}
                        </Text>
                        <Text>{error}</Text>
                    </MessageBarBody>
                </MessageBar>
            </div>
        );
    }

    const hasConversations = conversations.length > 0;

    return (
        <div className={styles.container}>
            <Card className={styles.summaryCard}>
                <div className={styles.header}>
                    <Button
                        icon={<ChevronLeft24Regular />}
                        appearance="subtle"
                        onClick={onBack}
                        className={styles.backButton}
                    >
                        {t('Common_BackToOverview') || 'Back to Overview'}
                    </Button>
                </div>

                <div className={styles.statsGrid}>
                    <div className={styles.statItem}>
                        <Caption1 className={styles.statLabel}>
                            {t('ConversationRatings_EmployeeName') ||
                                'Employee Name'}
                        </Caption1>
                        <Title3>{user.displayName}</Title3>
                    </div>

                    <div className={styles.statItem}>
                        <Caption1 className={styles.statLabel}>
                            {t('ConversationRatings_AverageRating') ||
                                'Average Rating'}
                        </Caption1>
                        <div className={styles.ratingContainer}>
                            <RatingStars rating={user.averageRating} />
                            <Title3 className={styles.ratingNumber}>
                                ({user.averageRating.toFixed(1)})
                            </Title3>
                        </div>
                    </div>

                    <div className={styles.statItem}>
                        <Caption1 className={styles.statLabel}>
                            {t('ConversationRatings_TotalConversations') ||
                                'Total Conversations'}
                        </Caption1>
                        <Title3 className={styles.statValue}>
                            {user.totalConversationsOwned}
                        </Title3>
                    </div>

                    <div className={styles.statItem}>
                        <Caption1 className={styles.statLabel}>
                            {t('ConversationRatings_RatedConversations') ||
                                'Rated Conversations'}
                        </Caption1>
                        <Title3 className={styles.statValue}>
                            {user.totalRatedConversations}
                        </Title3>
                    </div>

                    {user.averageFirstReplyTimeSeconds !== null && (
                        <div className={styles.statItem}>
                            <Caption1 className={styles.statLabel}>
                                {t('ConversationRatings_AvgFirstReplyTime') ||
                                    'Avg First Reply Time'}
                            </Caption1>
                            <Title3 className={styles.statValue}>
                                {formatElapsedMilliseconds(
                                    user.averageFirstReplyTimeSeconds * 1000
                                )}
                            </Title3>
                        </div>
                    )}

                    {user.averageHandlingTimeSeconds !== null && (
                        <div className={styles.statItem}>
                            <Caption1 className={styles.statLabel}>
                                {t('ConversationRatings_AvgHandlingTime') ||
                                    'Avg Handling Time'}
                            </Caption1>
                            <Title3 className={styles.statValue}>
                                {formatElapsedMilliseconds(
                                    user.averageHandlingTimeSeconds * 1000
                                )}
                            </Title3>
                        </div>
                    )}
                </div>
            </Card>

            {!hasConversations ? (
                <div className={styles.centerContent}>
                    <Body1>
                        {t('ConversationRatings_NoConversations') ||
                            'No rated conversations found for this user.'}
                    </Body1>
                </div>
            ) : (
                <div className={styles.tableContainer}>
                    <DataGrid
                        items={conversations}
                        columns={columns}
                        resizableColumns={true}
                        sortable={true}
                        columnSizingOptions={columnSizingOptions}
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {(column) => (
                                    <DataGridHeaderCell
                                        key={column.columnId}
                                        className={column.columnId.toString()}
                                    >
                                        {column.renderHeaderCell()}
                                    </DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody>
                            {(row) => (
                                <Tooltip
                                    content={
                                        t(
                                            'ConversationRatings_GoToConversationTooltip'
                                        ) || 'Go to conversation'
                                    }
                                    relationship="label"
                                    positioning="below"
                                >
                                    <DataGridRow
                                        key={row.rowId}
                                        className={styles.tableRow}
                                        onClick={(e: React.MouseEvent) =>
                                            handleRowClickEvent(
                                                e,
                                                row.item as ConversationRatingDetailDto
                                            )
                                        }
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Open Teams conversation"
                                    >
                                        {(column) => (
                                            <DataGridCell key={column.columnId}>
                                                {column.renderCell(
                                                    row.item as ConversationRatingDetailDto
                                                )}
                                            </DataGridCell>
                                        )}
                                    </DataGridRow>
                                </Tooltip>
                            )}
                        </DataGridBody>
                    </DataGrid>
                </div>
            )}
        </div>
    );
};

export default UserRatingsDetailList;
